import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactHtmlParser from 'html-react-parser';
import clsx from 'clsx';
import { useInView } from 'react-intersection-observer';
import { IconButton, MenuItem, makeStyles } from '@material-ui/core';
import { getRegExpByKeyword } from '@marqvision/shared/src/utils';
import { IcoCheckOutline, IcoFold, IcoHelpBorder, IcoUnfold } from '../../assets';
import MDSCheckbox from '../Checkbox';
import Tooltip, { MDSTooltip } from '../Tooltip';
import MDSTypography from '../Typography';
import MDSTheme from '../Theme';
import { DropItemContext, IDropDownItem, IDropDownProps, IDropItemContext } from '.';

const useStyles = makeStyles(() => ({
  tooltip: {
    display: 'inline',
    marginLeft: '4px',
  },
}));

export interface IDropDownItemProps {
  initialValue: IDropDownProps['value'];
  item: IDropDownItem;
  depth: number;
  onClose: () => void;
}

const ItemLabel = ({ item, depth, initialValue, onClose }: IDropDownItemProps) => {
  const classes = useStyles();

  const [imgSrc, setImgSrc] = useState<string>();
  const { ref, inView } = useInView();

  const menuRef = useRef<HTMLLIElement>(null);

  const {
    list,
    isMultiSelect,
    searchText,
    selectedValue,
    setSelectedValue,
    setFoldedList,
    getSelectedStatus,
    getIsFolded,
    close,
  } = useContext<IDropItemContext>(DropItemContext);

  const isFolded = getIsFolded(item);
  const [allChildValue, isSelected, isIndeterminate, isAllSelected] = getSelectedStatus(item);

  const renderHighlightText = (label: string) => {
    const regex = getRegExpByKeyword(searchText);
    const getConvertedText = (text: string): string => {
      return searchText ? text?.replace(regex, '<span class="highlight">$&</span>') : text;
    };

    return ReactHtmlParser(getConvertedText(label));
  };

  const handleGroupFold = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setFoldedList((ps) => (isFolded ? ps.filter((v) => v !== item.value) : [...ps, item.value]));
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    let target = e.target as Element;
    let needClose = false;
    while (target && target !== e.currentTarget) {
      if (target.tagName === 'BUTTON') {
        needClose = true;
        break;
      } else {
        target = target.parentNode as Element;
      }
    }
    if (needClose) {
      onClose();
    }
  };

  const handleItemClick = () => {
    if (item.disabled) return;

    if (isMultiSelect) {
      if (isSelected || isIndeterminate || isAllSelected) {
        //-1 전체선택일 경우
        if (Array.isArray(selectedValue) && selectedValue[0] === -1) {
          const filteredList: number[] = [];
          list.forEach((currItem) => {
            if (currItem.value !== item.value) {
              filteredList.push(currItem.value as number);
            }
          });

          setSelectedValue(filteredList);
        } else {
          setSelectedValue((ps) => ps.filter((value) => !allChildValue.includes(value)));
        }
      } else {
        setSelectedValue((ps) => [...ps, ...allChildValue.filter((value) => !ps.includes(value))]);
      }
    } else {
      setSelectedValue([item.value]);
      close(item.value);
    }
  };

  useEffect(() => {
    if (inView && item.imageUrl && imgSrc === undefined) {
      if (item.onView) {
        setImgSrc('');
        const fn = async () => {
          const presignedUrl = await item.onView?.();
          if (presignedUrl) {
            setImgSrc(presignedUrl);
          }
        };
        fn();
      } else {
        setImgSrc(item.imageUrl);
      }
    }
  }, [inView, imgSrc, item]);

  useEffect(() => {
    if (!isMultiSelect && initialValue === item.value) {
      if (menuRef.current) {
        menuRef.current.scrollIntoView({ block: 'center' });
      }
    }
  }, [isMultiSelect, initialValue, item.value]);

  if (
    (isMultiSelect && (item.children || item.value !== undefined)) ||
    (!isMultiSelect && !item.children && item.value !== undefined)
  ) {
    return (
      <MenuItem
        ref={menuRef}
        button
        onClick={handleItemClick}
        className={clsx('dropItem', {
          hasChild: item.children,
          hasImage: item.imageUrl,
          selectable: !item.disabled,
          disabled: item.disabled,
        })}
        style={{ paddingLeft: depth * 32 + 16 }}
        key={item.value as React.Key}
        tabIndex={isMultiSelect ? undefined : 0}
      >
        {isMultiSelect && (
          <MDSCheckbox
            indeterminate={isIndeterminate}
            className="dropCheckbox"
            isPrimary
            checked={isSelected || isAllSelected}
          />
        )}
        {item.imageUrl && (
          <div className="lazyImage">
            <img alt={imgSrc} src={imgSrc} ref={ref} />
          </div>
        )}
        {item.renderComponent ? (
          item.renderComponent(handleItemClick, onClose)
        ) : (
          <MDSTypography
            variant="T14"
            weight={!isMultiSelect && isSelected ? 'medium' : 'regular'}
            color={!isMultiSelect && isSelected ? MDSTheme.palette.blue[700] : MDSTheme.palette.bluegray[800]}
            lineClamp={2}
            style={{ flex: 'auto' }}
          >
            {!isMultiSelect && isSelected && <IcoCheckOutline className="selectIcon" />}
            {searchText && typeof item.label === 'string' ? renderHighlightText(item.label) : item.label}
            {item.help && (
              <MDSTooltip title={item.help} className={classes.tooltip}>
                <IcoHelpBorder width="16px" />
              </MDSTooltip>
            )}
          </MDSTypography>
        )}
        {item.children && (
          <IconButton className="expendButton" onClick={handleGroupFold}>
            {!isFolded ? <IcoFold /> : <IcoUnfold />}
          </IconButton>
        )}
      </MenuItem>
    );
  }

  return (
    <div onClick={handleClick}>
      {React.isValidElement(item.label) ? (
        item.label
      ) : (
        <div
          className={clsx('dropItem', { hasChild: item.children, hasImage: item.imageUrl, disabled: item.disabled })}
          style={{ paddingLeft: depth * 32 + 16 }}
        >
          {item.imageUrl && (
            <div className="lazyImage">
              <img alt={imgSrc} src={imgSrc} ref={ref} />
            </div>
          )}
          <MDSTypography variant="T14">
            {searchText ? renderHighlightText(item.label as string) : item.label}
          </MDSTypography>
          {item.children && (
            <IconButton className="expendButton" onClick={handleGroupFold}>
              {!isFolded ? <IcoFold /> : <IcoUnfold />}
            </IconButton>
          )}
        </div>
      )}
    </div>
  );
};

const DropDownItem = (props: IDropDownItemProps): JSX.Element => {
  const { getIsFolded } = useContext<IDropItemContext>(DropItemContext);

  const { item, depth, ...restProps } = props;
  const isFolded = getIsFolded(item);

  const BgColorByDepth = [MDSTheme.palette.white, MDSTheme.palette.bluegray[50], MDSTheme.palette.bluegray[100]];

  return (
    <div
      style={{ backgroundColor: BgColorByDepth[depth] || MDSTheme.palette.bluegray[100] }}
      key={item.value as React.Key}
    >
      <ItemLabel {...props} />
      {!isFolded &&
        item.children?.map((child: IDropDownItem) => (
          <DropDownItemWrap key={child.value as string} item={child} depth={depth + 1} {...restProps} />
        ))}
    </div>
  );
};

const DropDownItemWrap = (props: IDropDownItemProps) => {
  const { textLengthToShowTooltip } = useContext<IDropItemContext>(DropItemContext);
  const { item } = props;

  return textLengthToShowTooltip && typeof item.label === 'string' && textLengthToShowTooltip <= item.label.length ? (
    <Tooltip title={item.label} key={item.label}>
      <DropDownItem {...props} />
    </Tooltip>
  ) : (
    <DropDownItem {...props} />
  );
};

export default DropDownItemWrap;
